import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";

import { leiloesAtivos, listarCotacoesEmbarcador } from "services/leilaoFreteApi";
import { buscaCotacao, cidadeUf, cotacoesLeiloaveis, fmtBRL, fmtData } from "./regras";

// "+ Novo leilão" na tela de Leilões (13/10/2026): escolher a cotação que vai a
// leilão. Só aparecem as que podem ir agora (Pendente/Stand-by, versão
// corrente, sem leilão vivo); a escolha abre o NovoLeilaoModal.

const LIMITE_LISTA = 100;

export default function EscolherCotacaoModal({ isOpen, onClose, empresaId, onEscolher, onNovaCarga }) {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [cotacoes, setCotacoes] = useState([]);
  const [busca, setBusca] = useState("");

  useEffect(() => {
    if (!isOpen || !empresaId) return undefined;
    let ativo = true;
    setLoading(true);
    setErro(null);
    setBusca("");
    Promise.all([listarCotacoesEmbarcador(empresaId), leiloesAtivos(empresaId).catch(() => ({ porBase: {} }))])
      .then(([embarques, ativos]) => {
        if (!ativo) return;
        setCotacoes(cotacoesLeiloaveis(embarques, ativos && ativos.porBase));
      })
      .catch(() => {
        if (ativo) setErro("Não foi possível carregar as cotações.");
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [isOpen, empresaId]);

  const filtradas = useMemo(() => cotacoes.filter((c) => buscaCotacao(c, busca)), [cotacoes, busca]);
  const visiveis = filtradas.slice(0, LIMITE_LISTA);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" modalClassName="modal-align-top" contentClassName="lf-modal" backdrop="static" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>Novo leilão</span>
          <span className="lf-muted">nova carga ou cotação existente</span>
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <button type="button" className="lf-row is-clicavel lf-nova-carga" onClick={onNovaCarga}>
          <span className="lf-row-main">
            <span className="lf-row-nome">Nova carga</span>
            <span className="lf-cand-sub">
              Preencha os dados da carga e abra o leilão. O Hub grava a carga como cotação, que aparece em Minhas Cotações.
            </span>
          </span>
          <span className="lf-row-check is-on">Começar ›</span>
        </button>
        <div className="lf-section-title lf-ou">Ou leiloe uma cotação existente (Pendente ou Stand-by)</div>
        <Input bsSize="sm" className="lf-busca-cotacao" placeholder="Buscar por nº da cotação, pedido, cliente ou destino" value={busca} onChange={(e) => setBusca(e.target.value)} autoFocus />
        {loading ? (
          <div className="lf-empty-state">
            <Spinner size="sm" /> Carregando as cotações...
          </div>
        ) : erro ? (
          <div className="lf-aviso lf-aviso-danger">{erro}</div>
        ) : cotacoes.length === 0 ? (
          <div className="lf-empty-state">Nenhuma cotação Pendente ou Stand-by disponível para leilão.</div>
        ) : filtradas.length === 0 ? (
          <div className="lf-vazio">Nenhuma cotação com essa busca.</div>
        ) : (
          <>
            <div className="lf-muted lf-cot-contagem">
              {filtradas.length} cotaç{filtradas.length === 1 ? "ão disponível" : "ões disponíveis"}
              {filtradas.length > LIMITE_LISTA ? ` · mostrando as ${LIMITE_LISTA} mais recentes; use a busca` : ""}
            </div>
            <div className="lf-rows">
              {visiveis.map((c) => (
                <button key={c._id} type="button" className="lf-row is-clicavel lf-cot" onClick={() => onEscolher(c._id)}>
                  <span className="lf-row-main">
                    <span className="lf-row-nome">
                      <span className="lc-badge lc-badge-cotacao">{c.numeroCotacao != null ? `nº ${c.numeroCotacao}` : c.baseNumEmbarque || c.numEmbarque}</span>
                      {c.cliente || "Cliente"}
                      <span className="lf-rota-seta">→</span>
                      {cidadeUf({ cidade: c.cidadeDestino, uf: c.ufDestino })}
                    </span>
                    <span className="lf-cand-sub">
                      <span>{c.baseNumEmbarque || c.numEmbarque}</span>
                      {c.dataEmbarque ? <span>{fmtData(c.dataEmbarque)}</span> : null}
                      {c.transportadora ? <span>{c.transportadora}</span> : null}
                    </span>
                  </span>
                  <span className="lf-row-side">
                    <span className="lf-row-valor">{c.valor_total > 0 ? fmtBRL(c.valor_total) : "sem frete"}</span>
                    <span className="lf-badge lf-badge-neutral">{c.statusEmbarque}</span>
                    <span className="lf-row-check is-on">Leiloar ›</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="lc-btn-cancel" onClick={onClose}>
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
